/**
 * Currency Detection Utilities
 * 
 * Provides intelligent currency detection based on country codes
 * and merchant locations for international payment processing.
 */

/**
 * Maps ISO 3166-1 alpha-2 country codes to their primary currency codes
 */
export const getCountryCurrency = (countryCode: string): string => {
  if (!countryCode) return 'USD';
  
  const code = countryCode.toUpperCase();
  
  const currencyMap: Record<string, string> = {
    // Eurozone countries
    'AT': 'EUR', // Austria
    'BE': 'EUR', // Belgium
    'CY': 'EUR', // Cyprus
    'EE': 'EUR', // Estonia
    'FI': 'EUR', // Finland
    'FR': 'EUR', // France
    'DE': 'EUR', // Germany
    'GR': 'EUR', // Greece
    'IE': 'EUR', // Ireland
    'IT': 'EUR', // Italy
    'LV': 'EUR', // Latvia
    'LT': 'EUR', // Lithuania
    'LU': 'EUR', // Luxembourg
    'MT': 'EUR', // Malta
    'NL': 'EUR', // Netherlands
    'PT': 'EUR', // Portugal
    'SK': 'EUR', // Slovakia
    'SI': 'EUR', // Slovenia
    'ES': 'EUR', // Spain
    
    // Other European countries
    'GB': 'GBP', // United Kingdom
    'CH': 'CHF', // Switzerland
    'NO': 'NOK', // Norway
    'SE': 'SEK', // Sweden
    'DK': 'DKK', // Denmark
    'PL': 'PLN', // Poland
    'CZ': 'CZK', // Czech Republic
    'HU': 'HUF', // Hungary
    'RO': 'RON', // Romania
    'BG': 'BGN', // Bulgaria
    'HR': 'HRK', // Croatia
    'IS': 'ISK', // Iceland
    
    // Americas
    'US': 'USD', // United States
    'CA': 'CAD', // Canada
    'MX': 'MXN', // Mexico
    'BR': 'BRL', // Brazil
    'AR': 'ARS', // Argentina
    'CL': 'CLP', // Chile
    'CO': 'COP', // Colombia
    'PE': 'PEN', // Peru
    'UY': 'UYU', // Uruguay
    'PY': 'PYG', // Paraguay
    'BO': 'BOB', // Bolivia
    'EC': 'USD', // Ecuador (uses USD)
    'VE': 'VES', // Venezuela
    'GY': 'GYD', // Guyana
    'SR': 'SRD', // Suriname
    
    // Asia-Pacific
    'JP': 'JPY', // Japan
    'CN': 'CNY', // China
    'IN': 'INR', // India
    'KR': 'KRW', // South Korea
    'AU': 'AUD', // Australia
    'NZ': 'NZD', // New Zealand
    'SG': 'SGD', // Singapore
    'HK': 'HKD', // Hong Kong
    'TW': 'TWD', // Taiwan
    'TH': 'THB', // Thailand
    'ID': 'IDR', // Indonesia
    'MY': 'MYR', // Malaysia
    'PH': 'PHP', // Philippines
    'VN': 'VND', // Vietnam
    'BD': 'BDT', // Bangladesh
    'PK': 'PKR', // Pakistan
    'LK': 'LKR', // Sri Lanka
    
    // Middle East & Africa
    'AE': 'AED', // United Arab Emirates
    'SA': 'SAR', // Saudi Arabia
    'QA': 'QAR', // Qatar
    'KW': 'KWD', // Kuwait
    'BH': 'BHD', // Bahrain
    'OM': 'OMR', // Oman
    'JO': 'JOD', // Jordan
    'IL': 'ILS', // Israel
    'TR': 'TRY', // Turkey
    'EG': 'EGP', // Egypt
    'ZA': 'ZAR', // South Africa
    'NG': 'NGN', // Nigeria
    'KE': 'KES', // Kenya
    'GH': 'GHS', // Ghana
    'MA': 'MAD', // Morocco
    'TN': 'TND', // Tunisia
    'ET': 'ETB', // Ethiopia
    'UG': 'UGX', // Uganda
    'TZ': 'TZS', // Tanzania
    
    // Caribbean (many use their own or USD)
    'JM': 'JMD', // Jamaica
    'BB': 'BBD', // Barbados
    'TT': 'TTD', // Trinidad and Tobago
    'BS': 'BSD', // Bahamas
    'DO': 'DOP', // Dominican Republic
    'PR': 'USD', // Puerto Rico (US territory)
    
    // Pacific Islands
    'FJ': 'FJD', // Fiji
    'PG': 'PGK', // Papua New Guinea
    'SB': 'SBD', // Solomon Islands
    'VU': 'VUV', // Vanuatu
    'WS': 'WST', // Samoa
    'TO': 'TOP', // Tonga
    
    // Former Soviet States
    'RU': 'RUB', // Russia
    'UA': 'UAH', // Ukraine
    'BY': 'BYN', // Belarus
    'KZ': 'KZT', // Kazakhstan
    'UZ': 'UZS', // Uzbekistan
    'GE': 'GEL', // Georgia
    'AM': 'AMD', // Armenia
    'AZ': 'AZN', // Azerbaijan
  };
  
  return currencyMap[code] || 'USD';
};

/**
 * Determines the appropriate currency for a merchant based on their profile
 * Uses a hybrid approach: explicit preference > location-based > fallback
 */
export const getMerchantCurrency = (merchant: any): string => {
  // 1. Explicit preference (if merchant has set their preferred currency)
  if (merchant?.preferredCurrency) {
    console.log(`[Currency] Using merchant's preferred currency: ${merchant.preferredCurrency}`);
    return merchant.preferredCurrency;
  }
  
  // 2. Derive from merchant's primary address
  if (merchant?.addresses?.length > 0) {
    // Find primary address or use first one
    const primaryAddress = merchant.addresses.find((addr: any) => addr.isPrimary) || merchant.addresses[0];
    if (primaryAddress?.country) {
      const detectedCurrency = getCountryCurrency(primaryAddress.country);
      console.log(`[Currency] Detected ${detectedCurrency} from merchant location: ${primaryAddress.country}`);
      return detectedCurrency;
    }
  }
  
  // 3. Check if merchant has a location field directly
  if (merchant?.location?.country) {
    const detectedCurrency = getCountryCurrency(merchant.location.country);
    console.log(`[Currency] Detected ${detectedCurrency} from merchant location: ${merchant.location.country}`);
    return detectedCurrency;
  }
  
  // 4. Check merchant's business registration country
  if (merchant?.businessCountry) {
    const detectedCurrency = getCountryCurrency(merchant.businessCountry);
    console.log(`[Currency] Detected ${detectedCurrency} from business country: ${merchant.businessCountry}`);
    return detectedCurrency;
  }
  
  // 5. Fallback to USD
  console.log('[Currency] No merchant location found, defaulting to USD');
  return 'USD';
};

/**
 * Gets a list of commonly accepted currencies for a given country
 * Useful for showing currency options to merchants
 */
export const getCountryAcceptedCurrencies = (countryCode: string): string[] => {
  const primaryCurrency = getCountryCurrency(countryCode);
  const commonlyAccepted = new Set([primaryCurrency]);
  
  // Add commonly accepted international currencies based on region
  const code = countryCode?.toUpperCase();
  
  // European countries often accept EUR even if not in Eurozone
  if (['GB', 'CH', 'NO', 'SE', 'DK', 'PL', 'CZ', 'HU'].includes(code)) {
    commonlyAccepted.add('EUR');
  }
  
  // Many countries accept USD as secondary currency
  if (!['US', 'EC', 'PR'].includes(code)) { // Except countries that use USD as primary
    commonlyAccepted.add('USD');
  }
  
  // Tourist-heavy countries often accept multiple currencies
  if (['TR', 'EG', 'TH', 'MX', 'AE', 'SG', 'HK'].includes(code)) {
    commonlyAccepted.add('USD');
    commonlyAccepted.add('EUR');
  }
  
  // Border countries might accept neighbor's currency
  if (code === 'CA') commonlyAccepted.add('USD');
  if (code === 'MX') commonlyAccepted.add('USD');
  if (code === 'CH') commonlyAccepted.add('EUR');
  
  return Array.from(commonlyAccepted);
};

/**
 * Validates if a currency code is valid ISO 4217
 */
export const isValidCurrencyCode = (code: string): boolean => {
  if (!code || code.length !== 3) return false;
  
  // List of common valid currency codes (not exhaustive, but covers most cases)
  const validCodes = new Set([
    'USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 'CNY', 'INR',
    'KRW', 'SGD', 'HKD', 'NOK', 'SEK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON',
    'BGN', 'HRK', 'RUB', 'TRY', 'BRL', 'MXN', 'ARS', 'CLP', 'COP', 'PEN',
    'UYU', 'IDR', 'THB', 'MYR', 'PHP', 'VND', 'ZAR', 'AED', 'SAR', 'ILS',
    'EGP', 'NGN', 'KES', 'MAD', 'PKR', 'BDT', 'LKR', 'UAH', 'KZT'
  ]);
  
  return validCodes.has(code.toUpperCase());
};
